import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calculator, Building2, Receipt, BarChart3, Percent, FileText, IndianRupee, AlertTriangle, BookOpen, Landmark, Upload, HandCoins } from 'lucide-react';
import AccountingVendors from './AccountingVendors';
import AccountingExpenses from './AccountingExpenses';
import AccountingReports from './AccountingReports';
import AccountingTaxes from './AccountingTaxes';
import FinanceReceipts from './FinanceReceipts';
import AccountingVendorLedger from './AccountingVendorLedger';
import AccountingExceptions from './AccountingExceptions';
import AccountingBooks from './AccountingBooks';
import AccountingVendorBalances from './AccountingVendorBalances';
import AccountingStatementUploads from './AccountingStatementUploads';
import AccountingCreditsFees from './AccountingCreditsFees';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Accounting = () => {
  const [searchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'expenses');

  const fetchData = useCallback(async () => {
    try {
      const [vendorsRes, expensesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/accounting/vendors`),
        axios.get(`${BACKEND_URL}/api/accounting/expenses`)
      ]);
      setVendors(vendorsRes.data);
      setExpenses(expensesRes.data);
    } catch (err) {
      toast.error('Failed to load accounting data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <Calculator className="h-8 w-8 text-teal-700" />
            Accounting
          </h1>
          <p className="text-slate-500 mt-1">Manage vendors, track expenses, and view financial reports</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* GST/TCS report shortcuts moved into the Taxes tab (Phase 15.5
              bug fix) so the page header isn't dominated by tax buttons on
              tabs that have nothing to do with taxes. */}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100/80 p-1 rounded-xl" data-testid="accounting-tabs">
          <TabsTrigger value="expenses" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-expenses">
            <Receipt className="h-4 w-4" />Expenses
          </TabsTrigger>
          <TabsTrigger value="receipts" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-receipts">
            <IndianRupee className="h-4 w-4" />Receipts
          </TabsTrigger>
          <TabsTrigger value="vendors" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-vendors">
            <Building2 className="h-4 w-4" />Vendors
          </TabsTrigger>
          <TabsTrigger value="reports" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-reports">
            <BarChart3 className="h-4 w-4" />Reports
          </TabsTrigger>
          <TabsTrigger value="taxes" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-taxes">
            <Percent className="h-4 w-4" />Taxes
          </TabsTrigger>
          <TabsTrigger value="vendor-ledger" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-vendor-ledger">
            <IndianRupee className="h-4 w-4" />Vendor Ledger
          </TabsTrigger>
          <TabsTrigger value="exceptions" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-exceptions">
            <AlertTriangle className="h-4 w-4" />Exceptions
          </TabsTrigger>
          <TabsTrigger value="books" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-books">
            <BookOpen className="h-4 w-4" />Books
          </TabsTrigger>
          <TabsTrigger value="vendor-balances" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-vendor-balances">
            <Landmark className="h-4 w-4" />Vendor Balances
          </TabsTrigger>
          <TabsTrigger value="statement-uploads" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-statement-uploads">
            <Upload className="h-4 w-4" />Statement Uploads
          </TabsTrigger>
          <TabsTrigger value="credits-fees" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-credits-fees">
            <HandCoins className="h-4 w-4" />Credits & Fees
          </TabsTrigger>
        </TabsList>

        <TabsContent value="expenses" className="mt-6">
          <AccountingExpenses expenses={expenses} vendors={vendors} onRefresh={fetchData} />
        </TabsContent>
        <TabsContent value="receipts" className="mt-6">
          <FinanceReceipts embedded />
        </TabsContent>
        <TabsContent value="vendors" className="mt-6">
          <AccountingVendors vendors={vendors} expenses={expenses} onRefresh={fetchData} />
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <AccountingReports />
        </TabsContent>
        <TabsContent value="taxes" className="mt-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3 px-1">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Taxes</h2>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/reports/gst"
                data-testid="open-gst-report-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium transition-colors"
              >
                <FileText className="h-4 w-4" />
                GST Report
              </Link>
              <Link
                to="/reports/tcs"
                data-testid="open-tcs-report-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium transition-colors"
              >
                <FileText className="h-4 w-4" />
                TCS Report
              </Link>
            </div>
          </div>
          <AccountingTaxes />
        </TabsContent>
        <TabsContent value="vendor-ledger" className="mt-6">
          <AccountingVendorLedger vendors={vendors} />
        </TabsContent>
        <TabsContent value="exceptions" className="mt-6">
          <AccountingExceptions />
        </TabsContent>
        <TabsContent value="books" className="mt-6">
          <AccountingBooks />
        </TabsContent>
        <TabsContent value="vendor-balances" className="mt-6">
          <AccountingVendorBalances />
        </TabsContent>
        <TabsContent value="statement-uploads" className="mt-6">
          <AccountingStatementUploads />
        </TabsContent>
        <TabsContent value="credits-fees" className="mt-6">
          <AccountingCreditsFees />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Accounting;
